import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil, distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { AddressTypeEnum } from 'src/app/models/AddressTypeEnum';
import { City } from 'src/app/models/city';
import { Country } from 'src/app/models/Country';
import { DictionaryItem } from 'src/app/models/dictionary-item';
import { PostCode } from 'src/app/models/post-code';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'app-family-member-address-form',
  templateUrl: './family-member-address-form.component.html',
  styleUrls: ['./family-member-address-form.component.scss'],
  standalone: false
})
export class FamilyMemberAddressFormComponent implements OnInit, OnDestroy {
  private dictionaryService = inject(DictionaryService);

  AddressType = AddressTypeEnum;
  Country = Country;
  listOfCountries$: Observable<DictionaryItem[]>;
  listOfDistricts$: Observable<DictionaryItem[]>;

  listOfPoviats$: Observable<DictionaryItem[]>;
  listOfCommune$: Observable<DictionaryItem[]>;
  listOfCities$: Observable<City[]>;
  listOfStreets$: Observable<DictionaryItem[]>;
  listOfPostCodes$: Observable<{ Id: number, Name: string, City: City }[]>;

  selectedPoviat: DictionaryItem;
  selectedCommune: DictionaryItem;
  selectedCity: City;
  selectedStreet: DictionaryItem;

  @Input() familyMemberAddressForm: UntypedFormGroup;

  get countryControl() {
    return this.familyMemberAddressForm.get('country');
  }

  private readonly timeBetweemInput = 300;
  private readonly minimumInputLetters = 2;
  private readonly postCodePatternRx = /^\d{2}\-\d{1,3}/;

  private readonly unsubscribe$ = new Subject<void>();

  constructor() { }
  ngOnInit(): void {
    this.listOfCountries$ = this.dictionaryService.getCountriesForWorkerAddress().pipe(
      tap(countries => {
        const currentValueInList = countries.some(c => c.Id === this.countryControl?.value);
        if (countries.length <= 1 && currentValueInList) {
          this.countryControl?.disable();
        } else {
          this.countryControl?.enable();
        }
      }),
    );
    this.listOfDistricts$ = this.dictionaryService.getDistricts(Country.Poland);

    this.initializeAutoCompleteFields();
    this.onIsStayingInTheSameHouseholdChange();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }
  displayValue = (value: DictionaryItem | City | PostCode): string | undefined => (value ? value.Name : undefined);

  displayPostcodeValue = (value: any): string => {
    return typeof value === 'string' ? value : (value?.Name || '');
  };

  setSelectedPostCode($event: MatAutocompleteSelectedEvent): void {
    const option = $event.option.value;

    if (option.City.Commune.Poviat.District) {
      this.familyMemberAddressForm.get('polishAddress.postOffice')?.setValue(option.City.Name);

      this.familyMemberAddressForm.get('polishAddress.postcode')?.setValue(option.Name);

      this.familyMemberAddressForm.get('polishAddress.district')?.setValue(
        option.City.Commune.Poviat.District.Id,
        { emitEvent: true },
      );
      this.setSelectedPoviat(option.City.Commune.Poviat);
      this.setSelectedCommune(option.City.Commune);
      this.setSelectedCity(option.City);
      this.setSelectedStreet(undefined);
    }
  }

  onSelectionChange(event: any): void {
    this.onResetNextFormControls(event);
    this.familyMemberAddressForm.get('polishAddress.poviat').enable();
  }

  onResetNextFormControls(event: any): void {
    const addressControls = this.familyMemberAddressForm.get('polishAddress') as UntypedFormGroup;

    const controls = Object.keys(addressControls.controls);
    const filtredControls = controls.slice(controls.findIndex((x) => x === event?.source?.id || x === event?.target?.id) + 1);
    filtredControls.forEach((key) => {
      const control = addressControls.controls[key] as UntypedFormControl;
      control.reset();
      control.disable();
    });
  }
  setSelectedPoviat(poviat: DictionaryItem): void {
    if (poviat) {
      this.selectedPoviat = poviat;
      this.familyMemberAddressForm.get('polishAddress.poviat')?.setValue(poviat);
      this.familyMemberAddressForm.get('polishAddress.commune')?.enable();
    }
  }

  setSelectedCommune(commune: DictionaryItem): void {
    if (commune) {
      this.selectedCommune = commune;
      this.familyMemberAddressForm.get('polishAddress.commune')?.setValue(commune);
      this.familyMemberAddressForm.get('polishAddress.city')?.enable();
    }
  }
  setSelectedCity(city: City): void {
    if (city) {
      this.selectedCity = city;
      this.familyMemberAddressForm.get('polishAddress.city')?.setValue(city);
      this.familyMemberAddressForm.get('polishAddress.houseNumber')?.enable();
      this.familyMemberAddressForm.get('polishAddress.apartmentNumber')?.enable();
    }
    if (city?.HasStreets) {
      this.familyMemberAddressForm.get('polishAddress.street')?.enable();
    } else {
      this.familyMemberAddressForm.get('polishAddress.street')?.disable();
      this.familyMemberAddressForm.get('polishAddress.postcode')?.enable();
      this.familyMemberAddressForm.get('polishAddress.postOffice')?.enable();
    }
  }
  setSelectedStreet(street: DictionaryItem | undefined): void {
    if (street) {
      this.selectedStreet = street;
      this.familyMemberAddressForm.get('polishAddress.street')?.setValue(street);
      this.familyMemberAddressForm.get('polishAddress.postcode')?.enable();
      this.familyMemberAddressForm.get('polishAddress.postOffice')?.enable();
    }
  }

  private onIsStayingInTheSameHouseholdChange() {
    this.familyMemberAddressForm
      .get('isStayingInTheSameHousehold')
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((isStayingInTheSameHousehold: boolean) => {
        this.familyMemberAddressForm.markAsUntouched();
        if (isStayingInTheSameHousehold) {
          this.familyMemberAddressForm.get('polishAddress').reset();
          this.familyMemberAddressForm.get('abroadAddress').reset();
        }
      });
  }

  private initializeAutoCompleteFields() {
    this.listOfPoviats$ = this.familyMemberAddressForm.get('polishAddress.poviat').valueChanges.pipe(
      debounceTime(this.timeBetweemInput),
      switchMap((value: string) => {
        if (this.familyMemberAddressForm.get('polishAddress.district').value && value && value.length > this.minimumInputLetters) {
          return this.dictionaryService.getPoviats(this.familyMemberAddressForm.get('polishAddress.district').value, value);
        } else {
          return of(null);
        }
      }),
    );

    this.listOfCommune$ = this.familyMemberAddressForm.get('polishAddress.commune').valueChanges.pipe(
      debounceTime(this.timeBetweemInput),
      switchMap((value: string) => {
        if (this.selectedPoviat && value && value.length > this.minimumInputLetters) {
          return this.dictionaryService.getCommunes(this.selectedPoviat.Id, value);
        } else {
          return of(null);
        }
      }),
    );

    this.listOfCities$ = this.familyMemberAddressForm.get('polishAddress.city').valueChanges.pipe(
      debounceTime(this.timeBetweemInput),
      switchMap((value: string) => {
        if (this.selectedCommune && value && value.length > this.minimumInputLetters) {
          return this.dictionaryService.getCities(this.selectedCommune.Id, value);
        } else {
          return of(null);
        }
      }),
    );

    this.listOfStreets$ = this.familyMemberAddressForm.get('polishAddress.street').valueChanges.pipe(
      debounceTime(this.timeBetweemInput),
      switchMap((value: string) => {
        if (this.selectedCity && value && value.length > this.minimumInputLetters) {
          return this.dictionaryService.getStreets(this.selectedCity.Id, value);
        } else {
          return of(null);
        }
      }),
    );

    this.listOfPostCodes$ = this.familyMemberAddressForm.get('polishAddress.postcode')?.valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(this.timeBetweemInput),
      filter(value => {
        const postcodeString = typeof value === 'string' ? value : value?.Name;
        return postcodeString && this.postCodePatternRx.test(postcodeString);
      }),
      switchMap(value => {
        const postcodeString = typeof value === 'string' ? value : value?.Name;
        return this.dictionaryService.getPostCodes(postcodeString)
          .pipe(map((postCodes: PostCode[]) => postCodes.reduce((acc, postCode) => {
            acc = acc.concat(postCode.Cities.map(city => ({
              Id: postCode.Id,
              Name: postCode.Name,
              City: city
            })))
            return acc;
          }, [])));
      })
    ) || of([]);
  }
}
